import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { findObjectInArray } from '../../../../../../utils/filters';
import { stepTwoValidation } from '../../../../../../components/completingData/quoteCompletingSteps/validation';
import { typeDelivery } from '../../../../../../constants/general';
import RadioButton from '../../../../../../components/radioButton';

import geocodeAddress from '../../../../../../services/georref';

import Input from '../../../../../../components/input';

// import '../stepTwo.scss';
import './radioButtonItems.scss';

const RadioButtonItems = ({
  listFields,
  deliverySelected,
  errorsList,
  countryCode,
  onChange,
  formValues,
  updateFormValues,
  setErrorsList,
}) => {
  const [addressOptions, setAddressOptions] = useState([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [noResultsError, setNoResultsError] = useState(false);
  const [addressComplement, setAddressComplement] = useState('');
  const [baseAddress, setBaseAddress] = useState('');

  const debouncedGeocodeSearch = useCallback(
    (() => {
      let timeoutId;
      return (address, city, state) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (address && address.length > 3 && city && state) {
            setIsLoadingAddresses(true);
            try {
              const data = await geocodeAddress({
                country: 'COL',
                city: city.split('-')[0],
                state: state.split('-')[1],
                address,
              });
              const formatOptions = (dataOptions) => dataOptions.map((item) => ({
                ...item,
                address,
              }));
              const results = formatOptions(data?.response) || [];
              setAddressOptions(results);

              // Verificar si no hay resultados
              if (results.length === 0) {
                setNoResultsError(true);
              } else {
                setNoResultsError(false);
              }
            } catch (error) {
              console.error('Error al buscar direcciones:', error);
              setAddressOptions([]);
              setNoResultsError(true);
            } finally {
              setIsLoadingAddresses(false);
            }
          } else {
            setAddressOptions([]);
            setNoResultsError(false);
          }
        }, 1000);
      };
    })(),
    [],
  );

  useEffect(() => {
    onChange({
      values: formValues,
    });
  }, [formValues]);

  // Inicializar baseAddress cuando se carga el componente
  useEffect(() => {
    const currentAddress = formValues?.destinationAddress || '';
    if (currentAddress && !baseAddress) {
      const extractedBaseAddress = currentAddress.includes(',')
        ? currentAddress.split(',')[0].trim()
        : currentAddress;
      setBaseAddress(extractedBaseAddress);

      // Extraer las instrucciones de entrega si existen
      if (currentAddress.includes(',')) {
        const instructions = currentAddress.split(',').slice(1).join(',').trim();
        setAddressComplement(instructions);
      }
    }
  }, [formValues?.destinationAddress, baseAddress]);

  useEffect(() => {
    if (formValues.takeToOfficeOrCollection === 'takeToOffice') {
      updateFormValues('destinationAddress', '');
      updateFormValues('deliveryInstructions', '');
      setAddressComplement('');
      setBaseAddress('');
    }
  }, [formValues?.takeToOfficeOrCollection]);

  useEffect(() => {
    if (deliverySelected.officeAddress && !deliverySelected.pickupService) {
      updateFormValues('takeToOfficeOrCollection', 'takeToOffice');
    } else {
      updateFormValues('takeToOfficeOrCollection', 'collectionInAddress');
    }
  }, []);

  useEffect(() => {
    const destinyCity = formValues?.destiny;
    if (baseAddress && destinyCity) {
      debouncedGeocodeSearch(baseAddress, destinyCity, destinyCity);
    } else {
      setAddressOptions([]);
    }
  }, [baseAddress, formValues?.destiny, debouncedGeocodeSearch]);

  const geoOptions = () => {
    const options = addressOptions && addressOptions?.map((option) => option?.address);
    return options;
  };

  const findAddressOptionByValue = (value) => addressOptions.find(
    (option) => option.address === value);

  const updateAddressWithComplement = (address, complement) => {
    if (!address) return '';
    if (!complement || complement.trim() === '') return address;
    return `${address}, ${complement.trim()}`;
  };

  const textRadioButton = () => `Entrega en oficina de la ${typeDelivery[countryCode]}
                ${deliverySelected.officeAddress}`;
  return (
    <div className="radio-button-items">
      {listFields?.map(({
        name,
        typeField,
        children,
        checkedCollectionMethod,
        placeholder,
        label,
        type,
        annotation,
      }) => (
        <React.Fragment key={name}>
          {typeField === 'radioButton' && (
          <>
            {deliverySelected.pickupService && checkedCollectionMethod === 'collectionInAddress' && (
            <div className="radio-button-items__input-container">
              <RadioButton
                name={name}
                text=""
                value={checkedCollectionMethod}
                onChange={(value) => updateFormValues('takeToOfficeOrCollection', value)}
                checked={formValues?.takeToOfficeOrCollection === checkedCollectionMethod}
              >
                {children && children.map((it) => (
                  <Input
                    key={it.name}
                    value={formValues[it.name]?.split(',')[0] || formValues[it.name] || ''}
                    label={it.label}
                    onChange={(value) => {
                      const limitValue = value.toString();
                      const newBaseAddress = limitValue.slice(0, 200);

                      // Actualizar la dirección base para búsqueda
                      setBaseAddress(newBaseAddress);

                      // Concatenar con complemento para guardar en formValues
                      const finalAddress = updateAddressWithComplement(
                        newBaseAddress,
                        addressComplement,
                      );
                      updateFormValues(it.name, finalAddress);

                      // Verificar si es una selección del datalist
                      const selectedOption = findAddressOptionByValue(value);

                      if (selectedOption) {
                        if (selectedOption.lat) {
                          updateFormValues('destinyLatitude', selectedOption.lat);
                        }
                        if (selectedOption.lng) {
                          updateFormValues('destinyLongitude', selectedOption.lng);
                        }
                        if (selectedOption.postal_code) {
                          updateFormValues('destinyPostalCode', selectedOption.postal_code);
                        }
                        setAddressOptions([]);
                        setNoResultsError(false);
                      } else {
                        // Si no es una dirección válida del datalist, limpiar las coordenadas
                        updateFormValues('destinyLatitude', null);
                        updateFormValues('destinyLongitude', null);
                        updateFormValues('destinyPostalCode', '');
                        // Si cambia la dirección base, mantener las instrucciones
                        // Solo limpiar coordenadas, no las instrucciones
                      }
                      if (findObjectInArray(it.name, errorsList, 'name')) {
                        stepTwoValidation(
                          countryCode,
                          formValues,
                          errorsList,
                          setErrorsList,
                        );
                      }
                    }}
                    dataList={geoOptions()}
                    className={it.className ?? ''}
                    placeholder={it.placeholder}
                    annotationError={
                      findObjectInArray(it.name, errorsList, 'name')?.error
                      ?? findObjectInArray('geolocationRequired', errorsList, 'name')?.error
                      ?? (noResultsError ? 'Dirección no encontrada' : null)
                    }
                    type={it.type}
                    onFocus={() => updateFormValues('takeToOfficeOrCollection', 'collectionInAddress')}
                    isLoading={isLoadingAddresses}
                    inactiveDataList
                  />
                ))}
              </RadioButton>
              <Input
                label="Instrucciones de entrega"
                value={addressComplement}
                onChange={(value) => {
                  const limitValue = value.toString();
                  const limitedValue = limitValue.slice(0, 50);
                  setAddressComplement(limitedValue);

                  // Guardar instrucciones de entrega por separado
                  updateFormValues('deliveryInstructions', limitedValue);

                  // Concatenar con la dirección base actual
                  const finalAddress = updateAddressWithComplement(baseAddress, limitedValue);
                  updateFormValues('destinationAddress', finalAddress);

                  // Ejecutar validación siempre que cambie
                  stepTwoValidation(
                    countryCode,
                    { ...formValues, deliveryInstructions: limitedValue },
                    errorsList,
                    setErrorsList,
                  );
                }}
                placeholder="Incluye torre, apto, barrio, etc"
                type="text"
                className="address-complement-input"
                annotationError={findObjectInArray('deliveryInstructions', errorsList, 'name')?.error ?? null}
              />
            </div>
            )}
            {deliverySelected.officeAddress && checkedCollectionMethod === 'takeToOffice' && (
            <div className="radio-button-items__radio-container">
              <RadioButton
                name={name}
                value={checkedCollectionMethod}
                onChange={(value) => updateFormValues('takeToOfficeOrCollection', value)}
                checked={formValues?.takeToOfficeOrCollection === checkedCollectionMethod}
                annotationError={findObjectInArray('prohibitedItems', errorsList, 'name')?.error ?? null}
                text={textRadioButton()}
              />
            </div>
            )}
          </>
          )}
          {/* Input adicional que aparece cuando se selecciona entrega en oficina */}
          {formValues?.takeToOfficeOrCollection === 'takeToOffice' && name === 'receiverDocumentNumber' && (
            <div className="additional-field">
              <Input
                value={formValues[name]}
                onChange={(value) => updateFormValues(name, value)}
                placeholder={placeholder}
                label={label}
                annotation={annotation}
                annotationError={findObjectInArray(name, errorsList, 'name')?.error ?? null}
                type={type}
              />
            </div>
          )}
          {formValues?.takeToOfficeOrCollection === 'collectionInAddress' && name === 'reference' && countryCode === '484' && (
            <div className="additional-field">
              <Input
                value={formValues[name]}
                onChange={(value) => updateFormValues(name, value)}
                placeholder={placeholder}
                label={label}
                annotationError={findObjectInArray(name, errorsList, 'name')?.error ?? null}
                type={type}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

RadioButtonItems.propTypes = {
  listFields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  deliverySelected: PropTypes.shape({
    officeAddress: PropTypes.string,
    pickupService: PropTypes.bool,
  }).isRequired,
  formValues: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  updateFormValues: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  errorsList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  countryCode: PropTypes.string.isRequired,
  setErrorsList: PropTypes.func.isRequired,
};
export default RadioButtonItems;
